import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useParentNavigation } from '@/hooks/use-parent-navigation'
import { ContextMenuButton } from '@/components/ui/context-menu-button'
import { cn } from '@/lib/utils'

interface AppLinkProps {
  /** The internal route to navigate to (e.g., "/123" or "/123/version/2") */
  to: string
  /** Whether to replace the current URL or push a new one */
  replace?: boolean
  /** Content to render inside the link */
  children: React.ReactNode
  /** Optional className for styling */
  className?: string
  /** Callback when navigation is attempted */
  onNavigate?: () => void
  /** Additional props to pass to the underlying component */
  [key: string]: any
}

/**
 * A component that handles internal app navigation based on iframe context.
 * - In iframe: Renders as button (prevents new tab issues)
 * - Not in iframe: Renders as real Link (allows right-click, new tab, etc.)
 */
export function AppLink({
  to,
  replace = false,
  children,
  className,
  onNavigate,
  ...props
}: AppLinkProps) {
  const { shouldRenderNavigationLinks, navigate: parentNavigate } = useParentNavigation()
  const navigate = useNavigate()

  const handleOpen = () => {
    navigate(to, { replace })
    onNavigate?.()
  }

  const handleOpenInNewTab = () => {
    // For "open in new tab", use the parent navigation to open the full URL
    parentNavigate(to, { replaceUrl: false, openInNewTab: true, isInternalRoute: true })
    onNavigate?.()
  }

  // If in iframe context, render as button to prevent new tab issues
  if (shouldRenderNavigationLinks) {
    return (
      <ContextMenuButton
        className={className}
        onOpen={handleOpen}
        onOpenInNewTab={handleOpenInNewTab}
        {...props}
      >
        {children}
      </ContextMenuButton>
    )
  }

  // If not in iframe, render as real Link for full browser functionality. The click must not reach
  // an enclosing row that navigates on click, or a ctrl/cmd-click would also navigate this tab away.
  return (
    <Link
      to={to}
      replace={replace}
      className={className}
      onClick={(e) => { e.stopPropagation(); onNavigate?.() }}
      {...props}
    >
      {children}
    </Link>
  )
}